// Tipos de fila de la base de datos. Los repositorios devuelven filas CRUDAS de
// supabase-js, así que los campos van en snake_case, idénticos a Postgres.

export type Role =
  'admin' | 'comercial' | 'coordinador' | 'recurso' | 'datos' | 'supervisor' | 'comprador' | 'solicitante';
export type DocumentType = 'DNI' | 'CE';
export type ProfileStatus = 'activo' | 'inactivo' | 'bloqueado';
export type ApplicationStatus = 'pendiente' | 'aprobado' | 'rechazado' | 'bloqueado';
export type WorkerDocumentType = 'carnet_sanidad' | 'certificado_unico_laboral' | 'otro';
export type AuthorizationKind = 'tardanza' | 'falta' | 'permiso';
export type ReviewStatus = 'pendiente' | 'aprobado' | 'rechazado';
/** Desde 0013 el tipo de campaña es una FK a la maestra `campaign_types` (impulso,
 *  captadores, mercaderismo, mystery_shopper, auditoría, ventas, BTL, dinámica, otro…),
 *  editable sin migración: el conjunto ya NO es cerrado y por eso no se enumera aquí.
 *  La lista vigente se lee con `listCampaignTypes()`. */
export type CampaignType = string;
export type CampaignStatus = 'borrador' | 'activa' | 'cerrada';
export type RateUnit = 'jornada' | 'hora';
export type PositionStatus = 'abierta' | 'asignada' | 'cerrada';
export type AssignmentStatus = 'propuesta' | 'aprobada' | 'rechazada' | 'reemplazada';
export type AttendanceKind = 'ingreso' | 'salida';
/** El celular solo escribe 'online' y 'offline_sync'; las otras las escribe el servidor
 *  (carga histórica 0045, registro manual 0076, copia de GARRA 0263). */
export type AttendanceOrigin = 'online' | 'offline_sync' | 'carga_historica' | 'manual' | 'garra';
export type AttendanceStatus = 'pendiente' | 'validada' | 'observada';
/** `foto_repetida` la pone solo el servidor (0314). */
export type AttendanceFlag =
  'tardanza' | 'sin_gps' | 'lejos_del_pdv' | 'hora_sospechosa' | 'foto_repetida';
export type FormFieldKind = 'texto' | 'numero' | 'seleccion' | 'foto';
export type FormMoment = 'ingreso' | 'salida' | 'ambos';
export type ClosureStatus = 'borrador' | 'aprobado';
/** Procedencia de la coordenada del PDV: 'aproximada' es la que se cargó sin verificar
 *  (la que produce alertas falsas de "lejos del PDV"); las otras dos ya fueron revisadas. */
export type LocationSource = 'aproximada' | 'terreno' | 'mapa';

/** Fila de `profiles` tal como la LEE el frontend. NO incluye los datos de pago:
 *  `bank`, `account_number` y `cci` están revocados a `authenticated` a nivel de
 *  columna en 0002_rls.sql, así que el cliente nunca los recibe. Este es el tipo que
 *  usa todo el código de `app/src/` — por eso lleva el nombre corto. */
export interface ProfileRow {
  id: string;
  document_type: DocumentType;
  dni: string;
  first_names: string;
  last_names: string;
  phone: string | null;
  email: string;
  birth_date: string | null;
  department: string | null;
  province: string | null;
  district: string | null;
  address: string | null;
  zone: string | null;
  photo_path: string | null;
  doc_front_path: string | null;
  doc_back_path: string | null;
  roles: Role[];
  status: ProfileStatus;
  block_reason: string | null;
  blocked_by: string | null;
  blocked_at: string | null;
  created_at: string;
  /**
   * Recorre tiendas visitando a su equipo (0096). Es lo que habilita «Mi ruta» en el celular.
   *
   * ⚠️ NO es el rol: los supervisores de campo comparten el rol `coordinador` con quien coordina
   * desde la oficina, y solo a los primeros les corresponde. Y solo se LEE: el UPDATE está
   * revocado (0097), porque si el navegador pudiera escribirla, cualquiera se daría acceso a la
   * ruta —y a firmar fichas— desde la consola.
   */
  hace_ruta: boolean;
}

/** Columnas de `profiles` que `authenticated` puede LEER por PostgREST.
 *  `bank`, `account_number` y `cci` están revocados a nivel de columna en
 *  0002_rls.sql, así que `select('*')` sobre profiles falla con
 *  `42501 permission denied for column bank`. Usar SIEMPRE esta constante:
 *
 *      supabase.from('profiles').select(PROFILE_COLUMNS)
 *
 *  Los datos bancarios se obtienen solo con la RPC get_worker_payment_data(). */
export const PROFILE_COLUMNS =
  'id, document_type, dni, first_names, last_names, phone, email, birth_date, ' +
  'department, province, district, address, zone, ' +
  'photo_path, doc_front_path, doc_back_path, ' +
  'roles, status, block_reason, blocked_by, blocked_at, created_at, hace_ruta';

/** Alias histórico de `ProfileRow`: es exactamente lo que devuelve
 *  `select(PROFILE_COLUMNS)`. Se mantiene para que ambos nombres sean válidos. */
export type ProfileVisibleRow = ProfileRow;

/** Fila COMPLETA de `profiles`, con los datos de pago. Solo la puede leer el
 *  service role: la usa la Edge Function `approve-worker` al dar de alta a un
 *  trabajador. El frontend NUNCA recibe este shape. */
export interface ProfileFullRow extends ProfileRow {
  bank: string | null;
  account_number: string | null;
  cci: string | null;
}

/** Fila que devuelve la RPC `get_worker_payment_data(p_worker_id)` (solo admin). */
export interface WorkerPaymentData {
  worker_id: string;
  dni: string;
  first_names: string;
  last_names: string;
  bank: string | null;
  account_number: string | null;
  cci: string | null;
}

/** Documento laboral adjunto en la solicitud pública (columna `documents` jsonb). */
export interface ApplicationDocument {
  type: WorkerDocumentType;
  path: string;
  issue_date: string | null;
  expiry_date: string | null;
}

export interface WorkerApplicationRow {
  id: string;
  document_type: DocumentType;
  dni: string;
  first_names: string;
  last_names: string;
  phone: string | null;
  email: string | null;
  birth_date: string | null;
  department: string | null;
  province: string | null;
  district: string | null;
  address: string | null;
  zone: string | null;
  bank: string | null;
  account_number: string | null;
  cci: string | null;
  photo_path: string | null;
  doc_front_path: string | null;
  doc_back_path: string | null;
  documents: ApplicationDocument[];
  status: ApplicationStatus;
  review_note: string | null;
  reviewed_by: string | null;
  reviewed_at: string | null;
  created_at: string;
}

export interface WorkerDocumentRow {
  id: string;
  worker_id: string;
  type: WorkerDocumentType;
  label: string | null;
  file_path: string;
  issue_date: string | null;
  expiry_date: string | null;
  active: boolean;
  created_at: string;
}

export interface AuthorizationRow {
  id: string;
  worker_id: string;
  kind: AuthorizationKind;
  reason: string;
  event_date: string;
  evidence_path: string | null;
  status: ReviewStatus;
  reviewed_by: string | null;
  review_note: string | null;
  reviewed_at: string | null;
  created_at: string;
}

export interface PdvRow {
  id: string;
  code: string;
  name: string;
  chain: string | null;
  format: string | null;
  department: string | null;
  province: string | null;
  district: string | null;
  zone: string | null;
  address: string | null;
  lat: number | null;
  lng: number | null;
  radius_m: number;
  location_source: LocationSource;
  location_set_at: string | null;
  active: boolean;
  created_at: string;
}

export interface CampaignRow {
  id: string;
  /**
   * Nombre con el que la operación llama a la campaña ("Troperos Pringles"). Distinto de
   * cliente y marca: la hoja de programación trae los tres por separado. `null` en las
   * campañas viejas, y ahí las pantallas caen a "cliente — marca".
   */
  name: string | null;
  client: string;
  brand: string;
  type: CampaignType;
  description: string | null;
  quota: number | null;
  start_date: string;
  end_date: string;
  comercial_id: string | null;
  coordinator_id: string | null;
  status: CampaignStatus;
  /** Cuenta analítica (ej. S000169738): el código con el que la operación nombra la campaña. */
  analytic_account: string | null;
  /**
   * Fecha de archivado, o `null` si está visible (0027).
   *
   * Archivar reemplaza al borrado: borrar una campaña dejaba fotos huérfanas en el Storage —que
   * Supabase no permite borrar por SQL— y perdía el rastro de que existió.
   */
  archived_at: string | null;
  created_at: string;
  /**
   * La campaña entrega premios en el punto, así que su cierre de turno pregunta además por el
   * merch: cuántos entregó, cuántos le quedan y la foto de lo que deja en tienda (0115).
   *
   * Está detrás de una bandera y no de la marca porque Impulso Fijo lleva 54 cierres hechos con
   * el formulario de siempre y su equipo no fue capacitado en estas preguntas. Son campañas
   * distintas e independientes, y el formulario también.
   */
  cierre_con_canje: boolean;
  /** El cierre suma el bono global de asistencia (`app_settings.bono_asistencia_monto`) solo si
   *  es true (0267). GARRA no paga bono. */
  paga_bono_asistencia: boolean;
}

/** Fila de la maestra `campaign_types` (0013). */
export interface CampaignTypeRow {
  code: string;
  name: string;
  active: boolean;
  sort: number;
  created_at: string;
}

export interface CampaignScheduleRow {
  id: string;
  campaign_id: string;
  pdv_id: string;
  weekdays: number[];
  /** Fechas sueltas (YYYY-MM-DD) que se suman al patrón semanal (0207). */
  extra_dates: string[];
  /** Vigencia (0304). `null` = desde el inicio / hasta el fin de la campaña. `vigente_hasta` lo fija
   *  «Dar de baja desde…»; si queda en `vigente_desde − 1`, el horario no tiene días. */
  vigente_desde: string | null;
  vigente_hasta: string | null;
  start_time: string;
  end_time: string;
  headcount: number;
  rate_amount: number;
  rate_unit: RateUnit;
  created_at: string;
}

export interface CampaignPdvSupervisorRow {
  id: string;
  campaign_id: string;
  pdv_id: string;
  profile_id: string;
  created_at: string;
}

/** Fila de la tabla `positions`. Se llama PositionDbRow para no chocar con
 *  `PositionRow` de `domain/positions.ts` (el resultado de expandSchedule). */
export interface PositionDbRow {
  id: string;
  campaign_id: string;
  schedule_id: string;
  pdv_id: string;
  date: string;
  start_time: string;
  end_time: string;
  slot_index: number;
  rate_amount: number;
  rate_unit: RateUnit;
  status: PositionStatus;
  created_at: string;
}

export interface AssignmentRow {
  id: string;
  position_id: string;
  worker_id: string;
  status: AssignmentStatus;
  assigned_by: string | null;
  approved_by: string | null;
  approved_at: string | null;
  created_at: string;
}

export interface AttendanceRow {
  id: string;
  assignment_id: string;
  kind: AttendanceKind;
  device_ts: string;
  server_ts: string;
  lat: number | null;
  lng: number | null;
  accuracy: number | null;
  photo_path: string;
  origin: AttendanceOrigin;
  flags: AttendanceFlag[];
  status: AttendanceStatus;
  validated_by: string | null;
  validation_note: string | null;
  client_uuid: string;
  created_at: string;
  /** eTag de la foto en Storage (0314). La escribe solo el servidor; null si la foto no está. */
  foto_huella: string | null;
}

export interface FormFieldRow {
  id: string;
  campaign_id: string;
  label: string;
  kind: FormFieldKind;
  options: string[] | null;
  required: boolean;
  moment: FormMoment;
  sort: number;
  created_at: string;
  /**
   * Dinámica a la que está atado el campo (0029). `null` = se pide SIEMPRE en esa campaña.
   *
   * Con dinámica, el campo solo aparece donde y cuando esa dinámica está vigente: lo resuelve
   * `campos_del_turno` en el servidor. Es lo que hace que la pregunta de cierre del Raspa y Gana
   * se le pida a las 5 tiendas del fin de semana y a nadie más.
   */
  dynamic_id: string | null;
}

export interface FormResponseRow {
  id: string;
  attendance_id: string;
  field_id: string;
  value: string;
  created_at: string;
}

export interface PayrollClosureRow {
  id: string;
  campaign_id: string;
  period_start: string;
  period_end: string;
  status: ClosureStatus;
  approved_by: string | null;
  approved_at: string | null;
  created_at: string;
}

export interface PayrollLineRow {
  id: string;
  closure_id: string;
  worker_id: string;
  days_worked: number;
  hours_worked: number;
  amount: number;
  notes: string[];
  created_at: string;
}

export interface AuditLogRow {
  id: string;
  actor_id: string | null;
  action: string;
  entity: string;
  entity_id: string | null;
  detail: Record<string, unknown> | null;
  created_at: string;
}

export interface AppSettingRow {
  key: string;
  value: unknown;
  updated_at: string;
}
